import i18n from '../i18n'
import { aiDataConsentMessages } from '../i18n/aiDataConsent'
import { resolveSupportedLocale } from '../i18n/ui'

export { STUDIO_AI_DATA_CONSENT_VERSION as AI_DATA_CONSENT_VERSION } from '../../functions/src/aiDataConsentVersion'
import { STUDIO_AI_DATA_CONSENT_VERSION as AI_DATA_CONSENT_VERSION } from '../../functions/src/aiDataConsentVersion'
const keyFor = (userId: string) => `voxnela:ai-data-consent:${userId}`
const sessionGrants = new Set<string>()
let pending: { userId: string; promise: Promise<void> } | undefined

export const aiDataConsentCopy = (locale = i18n.resolvedLanguage ?? i18n.language) =>
  aiDataConsentMessages[resolveSupportedLocale(locale)]

export function hasAiDataConsent(userId: string): boolean {
  try {
    return sessionGrants.has(userId) || localStorage.getItem(keyFor(userId)) === AI_DATA_CONSENT_VERSION
  } catch { return sessionGrants.has(userId) }
}

export function revokeAiDataConsent(userId: string): void {
  sessionGrants.delete(userId)
  try { localStorage.removeItem(keyFor(userId)) } catch { /* Storage may be unavailable. */ }
}

/** Resolves only after an explicit opt-in; callers must await before any upload or AI call. */
export function requestAiDataConsent(userId: string, review = false): Promise<void> {
  if (!userId) return Promise.reject(new Error('Sign in before sharing data.'))
  if (pending) {
    if (pending.userId === userId) return pending.promise
    return Promise.reject(new Error('Another account has a pending consent request.'))
  }
  if (!review && hasAiDataConsent(userId)) return Promise.resolve()
  const copy = aiDataConsentCopy()
  const dialog = document.createElement('dialog')
  dialog.className = 'ai-data-consent'
  const locale = resolveSupportedLocale(i18n.resolvedLanguage ?? i18n.language)
  dialog.lang = locale
  dialog.dir = locale === 'ar' ? 'rtl' : 'ltr'
  dialog.setAttribute('aria-labelledby', 'ai-data-consent-title')
  const heading = document.createElement('h2')
  heading.id = 'ai-data-consent-title'
  heading.textContent = copy.title
  dialog.append(heading)
  for (const text of [copy.intro, copy.openai, copy.anthropic, copy.storage, copy.choice]) {
    const paragraph = document.createElement('p')
    paragraph.textContent = text
    dialog.append(paragraph)
  }
  const privacy = document.createElement('a')
  privacy.href = `https://voxnela.web.app/#/privacy?lang=${locale}`
  privacy.target = '_blank'
  privacy.rel = 'noopener'
  privacy.textContent = copy.privacy
  dialog.append(privacy)
  const actions = document.createElement('div')
  actions.className = 'ai-data-consent-actions'
  const decline = document.createElement('button')
  decline.type = 'button'
  decline.textContent = copy.decline
  const allow = document.createElement('button')
  allow.type = 'button'
  allow.className = 'ai-data-consent-allow'
  allow.textContent = copy.allow
  actions.append(decline, allow)
  dialog.append(actions)
  const promise = new Promise<void>((resolve, reject) => {
    const finish = (accepted: boolean) => {
      if (!dialog.isConnected) return
      if (accepted) {
        sessionGrants.add(userId)
        try { localStorage.setItem(keyFor(userId), AI_DATA_CONSENT_VERSION) } catch { /* Session consent still applies. */ }
      } else {
        revokeAiDataConsent(userId)
      }
      dialog.remove()
      pending = undefined
      if (accepted) resolve()
      else reject(new Error(copy.cancelled))
    }
    decline.onclick = () => finish(false)
    allow.onclick = () => finish(true)
    dialog.oncancel = (event) => { event.preventDefault(); finish(false) }
    document.body.append(dialog)
    try { dialog.showModal() } catch { dialog.remove(); reject(new Error(copy.cancelled)) }
  })
  pending = { userId, promise }
  // A failed dialog must never leave the consent gate stuck open.
  void promise.catch(() => { if (pending?.promise === promise) pending = undefined })
  return promise
}
